import {tagMapping} from "~/tag-mapping";

export const useStoreVideosSearch = defineStore('StoreVideosSearch', () => {

    const filters = ref([
        {
            "name": "Category",
            "field": "lesson_category",
            "currentValue": [],
            "values": [
                "Lesson",
                "Live Replay",
                "Song"
            ]
        },
        {
            "name": "Difficulty",
            "field": "lesson_difficulty",
            "currentValue": [],
            "values": [
                "Beginner",
                "Improver",
                "Intermediate",
                "Advanced"
            ]
        },
        {
            "name": "Instrument",
            "field": "lesson_instrument",
            "currentValue": [],
            "values": [
                "Acoustic",
                "Electric",
                "Ukulele"
            ]
        },
        {
            "name": "Artist",
            "field": "lesson_artist",
            "currentValue": [],
            "values": [], // Populated from API call
        },
        {
            "name": "Skill",
            "field": "lesson_skill",
            "currentValue": [],
            "values": [
                "Barre chords",
                "Open chords",
                "Power chords",
                "Riffs",
                "Solos"
            ]
        },
        {
            "name": "Genre",
            "field": "lesson_genre",
            "currentValue": [],
            "values": [], // Populated from API call
        },
        {
            "name": "Decade",
            "field": "lesson_decade",
            "currentValue": [],
            "values": [
                "50s and earlier",
                "60s",
                "70s",
                "80s",
                "90s",
                "2000s",
                "2010s",
                "2020s"
            ]
        },
        {
            "name": "Type",
            "field": "lesson_type",
            "currentValue": [],
            "values": [
                "\"Top\" lists",
                "2 chord song",
                "3 chord song",
                "4 chord song",
                "5 chord song"
            ]
        },
        {
            "name": "Grade",
            "field": "lesson_grade",
            "currentValue": [],
            "values": [
                "Grade 1",
                "Grade 2",
                "Grade 3",
                "Grade 4",
                "Grade 5",
                "Grade 6",
                "Grade 7"
            ]
        }
    ]);

    const QueryLessons = useQueryLessons();
    const QueryArtists = useQueryArtists();
    const QueryGenres = useQueryGenres();

    const EntityAction = useEntityAction();
    const UtilityFunctions = useUtilityFunctions();
    const router = useRouter();

    const lessons = ref(null);
    const error = ref(null);
    const query = ref('');
    const hasMore = ref(null);
    const limit = ref(60);
    const order = ref('recent');
    const orders = ref([
        {text: 'Most Recent', value: 'recent'},
        {text: 'Difficulty - Low to High', value: 'easy'},
        {text: 'Difficulty - High to Low', value: 'hard'},
    ]);
    const showMobileMenu = ref(false);

    // Returns an array of items that are currently selected from all filters
    const selected = computed(() => {
        const selected = [];
        filters.value.forEach(filter => {
            filter.currentValue.forEach(value => {
                selected.push({name: filter.name, field: filter.field, value});
            });
        });
        return selected;
    });

    // Enables / disables an option from a filter
    function toggleSelection ({name, value}) {
        const filter = filters.value.find(filter => filter.name === name);
        if (!filter) console.error(`Filter ${filter.name} not found`);
        if (filter.currentValue.includes(value)) {
            filter.currentValue = filter.currentValue.filter(v => v !== value);
        } else {
            filter.currentValue.push(value);
        }
        return getLessons();
    }

    // Run on page load
    async function init () {
        try {
            return Promise.all([
                getCategories(),
                getLessons({init: true})
            ]);
        } catch (e) {
            setError(e);
        }
    }

    async function getArtists () {
        const {artists, error} = await QueryArtists.get();
        if (error) {
            console.error(error);
            return;
        }
        filters.value.find(f => f.field === 'lesson_artist').values = artists;
    }

    async function getGenres () {
        const {genres, error} = await QueryGenres.get();
        if (error) {
            console.error(error);
            return;
        }
        filters.value.find(f => f.field === 'lesson_genre').values = genres;
    }

    async function getCategories () {
        return Promise.all([getArtists(), getGenres()]);
    }

    function updateFilterCurrentValue ({field, value}) {
        filters.value.find(filter => filter.field === field).currentValue = value;
        return getLessons();
    }

    function updateSortOrder (value) {
        order.value = value;
        return getLessons();
    }

    // Init defaults to false
    async function getLessons ({init} = {init: false}) {
        setError(null);

        // If it's a page load, get search criteria from query string
        if (init) {
            function setFiltersFromQueryString () {
                const queryStringObject = UtilityFunctions.cloneObject(router.currentRoute.value.query);
                order.value = queryStringObject.order || 'recent';

                // Only pre-defined filters are valid, prevents searching for random query strings added by referrers
                const validKeys = filters.value.map(filter => filter.field);
                // Clear any existing filters
                filters.value.forEach(filter => filter.currentValue = []);
                for(let key in queryStringObject) {
                    if (!validKeys.includes(key)) continue;
                    const f = filters.value.find(f => f.field === key);
                    f.currentValue = queryStringObject[key].split(',');
                }

                // Redirect links which included search criteria using old url tag scheme - mapping those tags to new lesson table fields
                const tags = queryStringObject?.tags?.split(',') || [];
                const mappedTags = tags.map(tag => tagMapping
                    .find(mappedTag => mappedTag.tag_slug === tag)) // Map the tag in the query string to a tag in the mapping
                    .filter(Boolean); // Only include tags which have a mapping and discard the rest
                for (const tag of mappedTags) {
                    const f = filters.value.find(f => f.field === tag.key);
                    if (!f) continue;
                    f.currentValue.push(tag.value);
                }
            }
            setFiltersFromQueryString();

            query.value = router.currentRoute.value.query.q;
        }

        // Build filter object for API
        let filterOrder;
        if (order.value === 'easy') filterOrder = [{field: 'lesson_order', asc: true}];
        if (order.value === 'hard') filterOrder = [{field: 'lesson_order', asc: false}];
        if (order.value === 'recent') filterOrder = [{field: 'lesson_created', asc: false}];
        filterOrder.push({field: 'id_lesson', asc: true}); // Secondary filter always by lesson_id - required to ensure consistency in pagination

        const filter = {
            fields: {},
            paginate: {
                start: 0,
                limit: limit.value,
                auto: true
            },
            query: {
                value: query.value,
                fields: [
                    'lesson_slug',
                    'lesson_title',
                ]
            },
            order: filterOrder,
        };

        // Set API filter object fields based on selected filters
        const categoryFilterFields = {};
        filters.value
            .filter(f => f.currentValue.length) // Only include ones where something is selected
            .forEach(f => {
                categoryFilterFields[f.field] = {"strings": f.currentValue}; // String search for all options selected for that field
            });
        Object.assign(filter.fields, categoryFilterFields);

        // Action tracking, but don't need to await the result of this
        EntityAction.postActionSongSearch(order.value, query.value, categoryFilterFields);

        return Promise.all([
            QueryLessons.filtered(filter, 'get-lessons-filtered')
                .then(({lessons: responseLessons, hasMore: responseHasMore, error: responseError}) => {
                    lessons.value = responseLessons;
                    hasMore.value = responseHasMore;
                    setError(responseError);
                }),
            updateQueryString(),
        ]);
    }

    function toggleMobileMenu () {
        setShowMobileMenu(!showMobileMenu.value);
    }

    function loadMore () {
        limit.value = limit.value + 60;
        return getLessons();
    }

    function setQuery (q) {
        query.value = q;
        return getLessons();
    }

    function updateQueryString () {
        const ui = router?.currentRoute.value?.query?.ui ;
        const title = router?.currentRoute.value?.query?.title;

        function generateFiltersQueryParameters () {
            const params = {};
            filters.value.forEach(filter => {
                if (filter.currentValue.length) {
                    params[filter.field] = filter.currentValue.join(',');
                }
            });
            return params;
        }

        const newRoute = {
            name: 'videos',
            query: {},
        };
        if (order.value) newRoute.query.order = order.value;
        if (query.value) newRoute.query.q = query.value;
        if (ui) newRoute.query.ui = ui;
        if (title) newRoute.query.title = title;

        newRoute.query = Object.assign(newRoute.query, generateFiltersQueryParameters());
        if (newRoute.query !== router.currentRoute.query) return router.replace(newRoute);
    }

    function clearAll () {
        filters.value.forEach(filter => filter.currentValue = []);
        return getLessons();
    }

    function setShowMobileMenu (data) { showMobileMenu.value = data; }
    function setError (e) {
        error.value = e ? e.alert : null;
    }

    return {
        lessons,
        error,
        query,
        hasMore,
        limit,
        order,
        orders,
        showMobileMenu,
        filters,
        selected,

        init,
        updateFilterCurrentValue,
        updateSortOrder,
        updateQueryString,
        getLessons,
        toggleMobileMenu,
        loadMore,
        setQuery,
        clearAll,
        setShowMobileMenu,
        toggleSelection,
    };

});

